import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Users, RefreshCw, Search, X } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';
import ConversationDialog from './ConversationDialog';
import StatTile from './shared/StatTile';
import CustomersTable from './customers/CustomersTable';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const AdminCustomers = ({ onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState(null);
  const [tagInputFor, setTagInputFor] = useState(null);
  const [tagDraft, setTagDraft] = useState('');
  const [threadEmail, setThreadEmail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (activeTag) params.tag = activeTag;
      const { data: resp } = await axios.get(`${API}/api/admin/customers`, {
        headers: authHeaders(),
        params,
      });
      setData(resp);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error(`Failed to load: ${apiErr(err)}`);
    } finally {
      setLoading(false);
    }
  }, [search, activeTag, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const updateTags = async (email, nextTags) => {
    try {
      await axios.put(
        `${API}/api/customer-notes`,
        { email, tags: nextTags },
        { headers: authHeaders() },
      );
      setData((prev) => prev && {
        ...prev,
        customers: prev.customers.map((c) => c.email === email ? { ...c, tags: nextTags } : c),
      });
      toast.success('Tags updated');
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    }
  };

  const addTag = (customer) => {
    const t = tagDraft.trim();
    if (!t) return;
    if (customer.tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setTagDraft('');
      return;
    }
    updateTags(customer.email, [...customer.tags, t]);
    setTagDraft('');
    setTagInputFor(null);
  };

  const removeTag = (customer, tag) => {
    updateTags(customer.email, customer.tags.filter((x) => x !== tag));
  };

  const customers = data?.customers || [];
  const allTags = data?.tags || [];

  const stats = useMemo(() => ({
    total: customers.length,
    returning: customers.filter((c) => c.status === 'returning').length,
    new: customers.filter((c) => c.status === 'lead').length,
    interactions: customers.reduce((s, c) => s + c.total_interactions, 0),
    revenue: customers.reduce((s, c) => s + (c.total_revenue || 0), 0),
  }), [customers]);

  return (
    <section data-testid="admin-customers-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-red-400" /> Customers
          </h2>
          <p className="text-sm text-gray-400">Every unique email across contacts &amp; quotes. Tag VIPs, returning, etc.</p>
        </div>
        <Button data-testid="customers-refresh" onClick={load} variant="outline" size="sm" className="border-slate-700 text-white hover:bg-slate-800">
          <RefreshCw className="w-3 h-3 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Total customers" value={stats.total} testid="customers-stat-total" />
        <StatTile label="Returning" value={stats.returning} testid="customers-stat-returning" />
        <StatTile label="One-time leads" value={stats.new} testid="customers-stat-new" />
        <StatTile label="Lifetime interactions" value={stats.interactions} testid="customers-stat-lti" />
        <StatTile label="Lifetime revenue" value={usd(stats.revenue)} testid="customers-stat-revenue" />
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                data-testid="customers-search"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white pl-9"
              />
            </div>
            {activeTag && (
              <Button
                size="sm" variant="outline"
                data-testid="customers-clear-tag"
                onClick={() => setActiveTag(null)}
                className="border-amber-500/40 text-amber-300 hover:bg-amber-950/30"
              >
                <X className="w-3 h-3 mr-1" /> Filter: {activeTag}
              </Button>
            )}
          </div>
          {allTags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-2">
              <span className="text-xs text-gray-500">Filter by tag:</span>
              {allTags.map((t) => (
                <button
                  key={t.tag}
                  type="button"
                  data-testid={`tag-filter-${t.tag}`}
                  onClick={() => setActiveTag(activeTag === t.tag ? null : t.tag)}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${
                    activeTag === t.tag
                      ? 'bg-amber-500/30 text-amber-100 border-amber-400'
                      : 'bg-slate-800 text-gray-300 border-slate-700 hover:bg-slate-700'
                  }`}
                >
                  {t.tag} <span className="text-gray-500">· {t.count}</span>
                </button>
              ))}
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {loading && !data ? (
            <p className="text-gray-400 py-6" data-testid="customers-loading">Loading customers…</p>
          ) : customers.length === 0 ? (
            <p className="text-gray-500 py-6 text-center" data-testid="customers-empty">No customers match.</p>
          ) : (
            <CustomersTable
              customers={customers}
              tagInputFor={tagInputFor}
              setTagInputFor={setTagInputFor}
              tagDraft={tagDraft}
              setTagDraft={setTagDraft}
              addTag={addTag}
              removeTag={removeTag}
              onOpenThread={setThreadEmail}
              onSendReviewRequest={async (c) => {
                try {
                  await axios.post(`${API}/api/admin/emails/send`, {
                    customer_email: c.email,
                    template_key: 'google_review_request',
                    first_name: (c.full_name || '').split(' ')[0],
                    last_name: (c.full_name || '').split(' ').slice(1).join(' '),
                  }, { headers: authHeaders() });
                  toast.success(`Review request sent to ${c.email}`);
                  load();
                } catch (err) { toast.error(`Send failed: ${apiErr(err)}`); }
              }}
              onToggleDNC={async (c) => {
                try {
                  await axios.patch(`${API}/api/admin/customers/${encodeURIComponent(c.email)}/communication`,
                    { do_not_contact: !c.do_not_contact },
                    { headers: authHeaders() });
                  toast.success(c.do_not_contact ? 'Do Not Contact removed' : 'Marked Do Not Contact');
                  load();
                } catch (err) { toast.error(`Update failed: ${apiErr(err)}`); }
              }}
            />
          )}
        </CardContent>
      </Card>

      <ConversationDialog
        open={Boolean(threadEmail)}
        onOpenChange={(v) => !v && setThreadEmail(null)}
        email={threadEmail}
        onThreadDeleted={() => { setThreadEmail(null); load(); }}
      />
    </section>
  );
};

export default AdminCustomers;
